"use client";

/**
 * Should this account see the "Mon réseau" entry at all, and does something await it?
 *
 * A head office always sees it — it is where the network is run. Everyone else sees it
 * ONLY once they have a reason to: a pending invitation, or a network they belong to.
 * An entry that opens on "your account belongs to no network" is a dead end in a menu.
 *
 * A head office costs no request: the flag is already on the account. Only a pro account
 * that is NOT a head office asks the server, and the answer is cached for the session —
 * this hook runs in the header, on every page.
 */

import { useEffect, useState } from "react";
import api from "@/app/lib/reducers/api";

const KEY = "gd_network_badge";

/** L'essentiel d'une invitation en attente : de qui elle vient, et quoi accepter. */
export type NetworkInvitation = {
  uuid: string;
  invitedAt?: string | null;
  head: { uuid: string; name: string | null; email: string } | null;
};

// `invitations` porte les MEMES lignes que `pending` compte. Le menu n'avait
// besoin que du nombre ; le tableau de bord doit nommer le siege qui invite et
// savoir quoi accepter. Les servir depuis la meme lecture evite un second appel
// a /api/network sur une page qui en declenche deja un dans l'en-tete.
export type NetworkBadge = { show: boolean; pending: number; invitations: NetworkInvitation[] };

// Une seule requete pour tous les appelants montes en meme temps. /account en
// compte cinq (l'en-tete, la coquille, le menu lateral, ses items, et la
// banniere d'invitation) : sans ce partage, un cache froid declenchait cinq
// GET /api/network identiques dans le meme rendu, la reponse n'etant ecrite en
// session qu'apres. Le meme motif que `usePayLaterOpenToAllPro`.
let inFlight: Promise<NetworkBadge> | null = null;

/** Call after accepting, declining or leaving: the cached answer is now wrong. */
export const invalidateNetworkBadge = () => {
  // La requete en cours porte la reponse d'AVANT : la laisser aboutir
  // reecrirait le cache que l'on vient de vider.
  inFlight = null;
  try { window.sessionStorage.removeItem(KEY); } catch { /* private mode */ }
};

export const useNetworkBadge = (user: any): NetworkBadge => {
  const isHead = user?.isHeadOffice === true;
  const isPro = user?.isPro === true;
  const [state, setState] = useState<NetworkBadge>({ show: isHead, pending: 0, invitations: [] });

  useEffect(() => {
    if (isHead) { setState({ show: true, pending: 0, invitations: [] }); return; }
    if (!isPro) { setState({ show: false, pending: 0, invitations: [] }); return; }

    try {
      const raw = window.sessionStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      // Une entree ecrite avant que `invitations` existe ne porte que le compte :
      // la relire telle quelle donnerait un tableau de bord sans invitation a
      // afficher alors que le menu en annonce une. On refait la lecture.
      if (parsed && Array.isArray(parsed.invitations)) { setState(parsed); return; }
    } catch { /* fall through to the request */ }

    let alive = true;
    if (!inFlight) {
      const request = api
        .get("/api/network")
        .then((r) => {
          const invitations: NetworkInvitation[] = (r.data?.pendingInvitations || []).map((inv: any) => ({
            uuid: inv?.uuid,
            invitedAt: inv?.invitedAt ?? null,
            head: inv?.head || null,
          }));
          const next: NetworkBadge = {
            pending: invitations.length,
            show: !!r.data?.membership || invitations.length > 0,
            invitations,
          };
          // Ecrit seulement si personne n'a invalide entre-temps.
          if (inFlight === request) {
            try { window.sessionStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
          }
          return next;
        })
        // Hidden on failure rather than shown: a menu entry that leads to an error is
        // worse than no entry, and the email link remains a way in either way.
        .catch(() => ({ show: false, pending: 0, invitations: [] } as NetworkBadge))
        .finally(() => { if (inFlight === request) inFlight = null; });
      inFlight = request;
    }
    inFlight.then((next) => { if (alive) setState(next); });
    return () => { alive = false; };
  }, [isHead, isPro, user?.uuid]);

  return state;
};
